import React, { useState, useEffect } from 'react';
import { IconUp } from '../common/Icons';
import { useI18n } from '../../services/i18n';
import { TIERS, priceForTopup, money, fmtDate, PRICE_PER_HOUR } from '../../services/api';

export default function Wallet({
  me,
  topup,
  setTopup,
  transactions,
  loadTransactions,
  onTopup,
  busy
}) {
  const { t, lang } = useI18n();
  const [inputVal, setInputVal] = useState(String(topup || 50));

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  useEffect(() => {
    setInputVal(String(topup));
  }, [topup]);

  const numAmount = Math.max(0, Math.floor(parseFloat(inputVal.replace(',', '.')) || 0));
  const currentPrice = priceForTopup(numAmount);
  const hours = numAmount > 0 ? Math.floor((numAmount / currentPrice) * 10) / 10 : 0;

  let upsell = null;
  if (numAmount > 0 && numAmount < 50) {
    upsell = {
      to: 50,
      text: t('upsell_50', { diff: 50 - numAmount })
    };
  } else if (numAmount >= 50 && numAmount < 200) {
    upsell = {
      to: 200,
      text: t('upsell_200', {
        h200: Math.floor(200 / 7),
        hCurrent: Math.floor(200 / 8)
      })
    };
  }

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputVal(v);
    const parsed = Math.max(0, Math.floor(parseFloat(v.replace(',', '.')) || 0));
    setTopup(parsed);
  };

  const handleChipClick = (val) => {
    setInputVal(String(val));
    setTopup(val);
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light');
  };

  const handleUpsellClick = (targetVal) => {
    setInputVal(String(targetVal));
    setTopup(targetVal);
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light');
  };

  const handlePayClick = () => {
    if (numAmount >= 1 && !busy) {
      onTopup(numAmount);
    }
  };

  return (
    <>
      <main
        className="screen"
        style={{ paddingBottom: 'calc(var(--tabbar-h) + 100px)' }}
        data-testid="screen-wallet"
      >
        <h1>{t('wallet_title')}</h1>

        {/* Balance summary card */}
        <div className="card" style={{ gap: '6px' }}>
          <span className="small muted" style={{ fontWeight: 600 }}>{t('available_label')}</span>
          <span className="balance-big" data-testid="wallet-balance">
            {me ? money(me.balance) : '…'}
          </span>
          <span style={{ fontSize: '14px', color: 'var(--text-2)' }}>
            {me
              ? t('hours_estimate', {
                  hours: Math.floor(me.balance / PRICE_PER_HOUR),
                  price: PRICE_PER_HOUR
                })
              : ''}
          </span>
        </div>

        {/* Pricing tiers */}
        <section className="col">
          <span className="label">{t('tiers_title')}</span>
          <div className="tiers" data-testid="pricing-tiers">
            {TIERS.map((tierItem) => {
              const isOn = tierItem.price === currentPrice;
              const rangeText = tierItem.rangeKey ? t(tierItem.rangeKey) : tierItem.range;
              const noteText = tierItem.noteKey?.startsWith('tier') ? t(tierItem.noteKey) : tierItem.note;
              return (
                <div
                  key={tierItem.price}
                  className={`tier ${isOn ? 'is-on' : ''}`}
                  data-testid={`tier-${tierItem.price}`}
                >
                  <small>{rangeText}</small>
                  <b>${tierItem.price}</b>
                  <small>{noteText}</small>
                </div>
              );
            })}
          </div>
        </section>

        {/* Topup amount section */}
        <section className="col">
          <label className="label" htmlFor="amount-input">{t('topup_amount_label')}</label>
          <div className="field field--big">
            <span style={{ fontSize: '24px', fontWeight: 800, color: 'var(--muted)' }}>$</span>
            <input
              id="amount-input"
              inputMode="decimal"
              value={inputVal}
              onChange={handleInputChange}
              data-testid="topup-input"
            />
            <span
              id="amount-hint"
              style={{
                fontSize: '14px',
                fontWeight: 700,
                color: 'var(--accent-soft-text)',
                whiteSpace: 'nowrap'
              }}
              data-testid="topup-hint"
            >
              {t('amount_hint', { hours, price: currentPrice })}
            </span>
          </div>

          <div className="amounts">
            {[20, 50, 100, 200].map((v) => (
              <button
                key={v}
                className={`chip ${v === numAmount ? 'is-on' : ''}`}
                onClick={() => handleChipClick(v)}
                data-testid={`amount-chip-${v}`}
              >
                ${v}
              </button>
            ))}
          </div>

          {upsell && (
            <button
              className="note note--info"
              onClick={() => handleUpsellClick(upsell.to)}
              data-testid="btn-upsell"
            >
              <IconUp />
              <span>{upsell.text}</span>
            </button>
          )}
        </section>

        {/* Transactions list */}
        <section className="col">
          <span className="label">{t('transactions_title')}</span>
          {transactions === null ? (
            <div className="skeleton" style={{ height: '120px' }} data-testid="tx-loading" />
          ) : transactions.length > 0 ? (
            <div className="list" data-testid="tx-list">
              {transactions.map((transactionItem) => (
                <div className="list__row" style={{ padding: '12px 0' }} key={transactionItem.id}>
                  <span className="col" style={{ gap: '2px' }}>
                    <b>{transactionItem.title}</b>
                    <span className="small muted" style={{ fontSize: '12px' }}>
                      {fmtDate(transactionItem.at, lang)}
                    </span>
                  </span>
                  <b style={{ color: transactionItem.amount > 0 ? 'var(--info-text)' : 'var(--text-2)' }}>
                    {transactionItem.amount > 0 ? '+' : '−'}{money(Math.abs(transactionItem.amount))}
                  </b>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty" data-testid="tx-empty">
              <span className="small muted">{t('tx_empty')}</span>
            </div>
          )}
        </section>
      </main>

      {/* Fixed bottom payment button */}
      <div className="foot" data-testid="wallet-foot">
        <div className="foot__inner">
          <button
            className="btn btn--primary"
            onClick={handlePayClick}
            disabled={numAmount < 1 || busy}
            data-testid="btn-pay-crypto"
          >
            {busy ? t('btn_pay_creating') : `${t('btn_pay_crypto', { amount: numAmount })} `}
            <span style={{ fontSize: '12px', opacity: 0.75 }}>{t('crypto_currencies')}</span>
          </button>
        </div>
      </div>
    </>
  );
}
